"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.executeComposureRulesets = exports.executeRulesets = exports.buildActiveRulesets = exports.buildComposure = exports.buildForms = exports.pairIdsAndOutcomes = exports.buildDataV2 = exports.buildData = void 0;
const htmlparser2_1 = require("htmlparser2");
const functional_1 = require("../../utils/functional");
const interpolate_string_1 = __importDefault(require("../../utils/interpolate-string"));
const resolvers_1 = require("../../utils/resolvers");
const ruleset_parser_1 = __importDefault(require("./ruleset-parser"));
const reporter_1 = __importDefault(require("../../reporter"));
// Iterate over activeRulesets, get data via query and attr,
// then return an array containing data, RulesetID, and url
// for each activeRuleset (later rulesets with the same action url will
// be merged before being submitted
const buildData = ({ activeRulesets, models }) => 
// Next line converts object from { foo: 'bar' } to [['foo', 'bar']]
// for easy iteration
functional_1.pipe.objToArr(activeRulesets)((0, functional_1.filter)(([name, attrs]) => {
    if (!attrs.data) {
        reporter_1.default.warn(`Could not find data for ruleset-submission: ${name}`, attrs);
        return false;
    }
    return true;
}), 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
(0, functional_1.reduce)((acc, [k, v]) => {
    const { data } = v;
    const resolvedData = (0, functional_1.reduce)((acc1, { name, modelRef }) => ({
        ...acc1,
        ...(0, resolvers_1.resolveValues)({
            name,
            modelRef,
            models,
        }),
    }), {})(data);
    return [
        ...acc,
        {
            ...resolvedData,
            RulesetID: v.idref,
            url: v.action,
        },
    ];
}, []));
exports.buildData = buildData;
const buildDataV2 = ({ activeRulesets, model }) => 
// Next line converts object from { foo: 'bar' } to [['foo', 'bar']]
// for easy iteration
functional_1.pipe.objToArr(activeRulesets)((0, functional_1.filter)(([name, attrs]) => {
    if (!attrs.data) {
        reporter_1.default.warn(`Could not find data for ruleset-submission: ${name}`, attrs);
        return false;
    }
    return true;
}), 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
(0, functional_1.reduce)((acc, [k, v]) => {
    const { data } = v;
    const resolvedData = (0, functional_1.reduce)((acc1, { name, term, value }) => ({
        ...acc1,
        [name]: value || model.terms[term]?.value || '',
    }), {})(data);
    return [
        ...acc,
        {
            ...resolvedData,
            RulesetID: v.idref,
            url: v.action,
        },
    ];
}, []));
exports.buildDataV2 = buildDataV2;
const mergeSameUrls = (acc, data) => {
    const { url, RulesetID, ...modelData } = data;
    const existing = acc[url];
    return {
        ...acc,
        [url]: existing
            ? {
                ...existing,
                ...modelData,
                RulesetID: [...existing.RulesetID, RulesetID],
            }
            : {
                ...modelData,
                RulesetID: [RulesetID],
            },
    };
};
const buildForm = ([url, data]) => {
    // eslint-disable-next-line no-undef
    const formData = new FormData();
    functional_1.pipe.objToArr(data)((0, functional_1.map)(([k, v]) => formData.append(k, v)));
    return [url, formData];
};
const makeRequest = async ({ url, data, config }) => {
    // eslint-disable-next-line no-undef
    const resp = await fetch((0, interpolate_string_1.default)(url, config), {
        method: 'POST',
        body: data,
    });
    return resp.text();
};
const executeRequests = (config) => (data) => (0, functional_1.objToArr)(data).reduce((acc, [url, requestData]) => makeRequest({
    url,
    data: requestData,
    config,
}).then((resp) => [...acc, resp]), []);
const handleResponses = (data) => data.map((xml) => {
    const handler = new ruleset_parser_1.default();
    new htmlparser2_1.Parser(handler, {
        xmlMode: true,
    }).end(xml);
    return handler.result;
});
const mergeOutcomes = (idsAndOutcomes) => (data) => data.reduce((acc, ruleOutcomes) => [
    ...acc,
    ...ruleOutcomes.map((ruleset) => ({
        ...ruleset,
        ...idsAndOutcomes[ruleset.rulesetId],
    })),
], []);
const pairIdsAndOutcomes = ({ rulesets }) => functional_1.pipe.objToArr(rulesets)((0, functional_1.reduce)(
// eslint-disable-next-line @typescript-eslint/no-unused-vars
(acc, [_, { idref, outcome, highlightControls }]) => idref
    ? {
        ...acc,
        [idref]: {
            outcome,
            highlightControls: highlightControls
                ? highlightControls.split(' ')
                : [],
        },
    }
    : acc, {}));
exports.pairIdsAndOutcomes = pairIdsAndOutcomes;
const buildForms = ({ activeRulesets }) => (models) => {
    const data = (0, exports.buildData)({
        activeRulesets,
        models,
    });
    return (0, functional_1.pipe)(data)((0, functional_1.reduce)(mergeSameUrls, {}), functional_1.objToArr, (0, functional_1.map)(buildForm), functional_1.intoObj);
};
exports.buildForms = buildForms;
const buildComposure = ({ activeRulesets }) => (model) => {
    const data = (0, exports.buildDataV2)({
        activeRulesets,
        model,
    });
    return (0, functional_1.pipe)(data)((0, functional_1.reduce)(mergeSameUrls, {}), functional_1.objToArr, (0, functional_1.map)(buildForm), functional_1.intoObj);
};
exports.buildComposure = buildComposure;
const buildActiveRulesets = ({ localRulesets, rulesets }) => Object.assign({}, ...localRulesets.map((rule) => ({
    [rule]: rulesets[rule],
})));
exports.buildActiveRulesets = buildActiveRulesets;
// This function iterates over rulesets, sends the network request,
// and updates the models appropriately if the ruleset returns "YES"
const executeRulesets = ({ rulesets, localRulesets, config }) => (models) => {
    const activeRulesets = (0, exports.buildActiveRulesets)({
        localRulesets,
        rulesets,
    });
    const idsAndOutcomes = (0, exports.pairIdsAndOutcomes)({
        rulesets,
    });
    return functional_1.pipe.async(models)((0, exports.buildForms)({
        activeRulesets,
    }), executeRequests(config), handleResponses, mergeOutcomes(idsAndOutcomes));
};
exports.executeRulesets = executeRulesets;
const executeComposureRulesets = ({ rulesets, localRulesets, config, model, }) => {
    const activeRulesets = (0, exports.buildActiveRulesets)({
        localRulesets,
        rulesets,
    });
    const idsAndOutcomes = (0, exports.pairIdsAndOutcomes)({
        rulesets,
    });
    return functional_1.pipe.async(model)((0, exports.buildComposure)({
        activeRulesets,
    }), executeRequests(config), handleResponses, mergeOutcomes(idsAndOutcomes));
};
exports.executeComposureRulesets = executeComposureRulesets;
//# sourceMappingURL=index.js.map